import React, { useEffect, useRef } from 'react';

export function ChatAndMembers({
  messages,
  members,
  newMessage,
  setNewMessage,
  isSendingMessage,
  attendeeUuid,
  onSendMessage,
}) {
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="mb-6 rounded-lg bg-gray-900 border border-gray-800 overflow-hidden">
      <div className="flex flex-col md:flex-row h-[600px]">
        {/* Members List - Left Side */}
        <div className="w-full md:w-72 border-b md:border-b-0 md:border-r border-gray-800 bg-gray-900/50 flex flex-col">
          <div className="p-4 border-b border-gray-800 flex items-center gap-2">
            <span className="text-lg">👥</span>
            <h3 className="font-bold text-white">Members ({members.length})</h3>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {members.length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-sm">
                No members yet
              </div>
            ) : (
              <div className="space-y-2">
                {members.map((member, index) => {
                  const isCurrentUser = member.attendee_uuid === attendeeUuid;
                  return (
                    <div
                      key={index}
                      className={`p-3 rounded-lg flex items-center gap-3 ${
                        isCurrentUser
                          ? 'bg-[#4ADE80]/20 border border-[#4ADE80]/30'
                          : 'bg-gray-800/50 border border-gray-700/50'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-[#4ADE80] flex items-center justify-center text-[#050505] font-semibold text-sm">
                        {member.attendee_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${isCurrentUser ? 'font-semibold text-white' : 'text-gray-300'}`}>
                            {member.attendee_name}
                          </span>
                          {isCurrentUser && (
                            <span className="rounded-full bg-[#4ADE80] px-2 py-0.5 text-xs font-semibold text-[#050505]">
                              You
                            </span>
                          )}
                          {member.is_host && (
                            <span className="rounded-full bg-gray-600 px-2 py-0.5 text-xs font-semibold text-white">
                              Captain
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Chat Section - Right Side */}
        <div className="flex-1 flex flex-col">
          {/* Chat Header */}
          <div className="p-4 border-b border-gray-800 flex items-center gap-2">
            <span className="text-lg">💬</span>
            <h3 className="font-bold text-white">Chat</h3>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 bg-gray-900/30">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-400 text-sm">No messages yet. Start the conversation!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((msg) => {
                  const storedName = typeof window !== 'undefined' 
                    ? localStorage.getItem('attendee_name') 
                    : null;
                  const isSystemMessage = msg.attendee_name === 'CrowdOne';
                  const isOwnMessage = 
                    !isSystemMessage &&
                    ((msg.attendee_uuid && msg.attendee_uuid === attendeeUuid) ||
                    (!msg.attendee_uuid && msg.attendee_name === storedName));
                  
                  const messageDate = new Date(msg.created_at);
                  const timeStr = messageDate.toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit',
                  });
                  
                  // System messages are centered
                  if (isSystemMessage) {
                    return (
                      <div
                        key={msg.id}
                        className="flex justify-center"
                      >
                        <div className="max-w-[80%] rounded-lg p-4 bg-blue-900/30 border border-blue-700/50">
                          <div className="flex items-center gap-2 mb-2 justify-center">
                            <span className="text-xs font-semibold text-blue-400">
                              {msg.attendee_name}
                            </span>
                            <span className="text-xs text-gray-500">
                              {timeStr}
                            </span>
                          </div>
                          <p className="text-sm text-gray-200 break-words whitespace-pre-line text-center">
                            {msg.message}
                          </p>
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg p-3 ${
                          isOwnMessage
                            ? 'bg-[#4ADE80]/20 border border-[#4ADE80]/30'
                            : 'bg-gray-800 border border-gray-700'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold ${
                            isOwnMessage ? 'text-[#4ADE80]' : 'text-gray-300'
                          }`}>
                            {isOwnMessage ? 'You' : msg.attendee_name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {timeStr}
                          </span>
                        </div>
                        <p className="text-sm text-gray-200 break-words">
                          {msg.message}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Message Input */}
          <div className="p-4 border-t border-gray-800">
            <div className="flex gap-2 items-end">
              <textarea
                value={newMessage}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 500) {
                    setNewMessage(val);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && newMessage.trim() && !isSendingMessage && newMessage.length <= 500) {
                    e.preventDefault();
                    onSendMessage();
                  }
                }}
                placeholder="Type a message..."
                disabled={isSendingMessage}
                rows={1}
                className="flex-1 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-white placeholder-gray-500 focus:border-[#4ADE80] focus:outline-none focus:ring-2 focus:ring-[#4ADE80] resize-none"
              />
              <button
                onClick={onSendMessage}
                disabled={!newMessage.trim() || isSendingMessage || newMessage.length > 500}
                className="rounded-lg bg-[#4ADE80] px-6 py-2 font-semibold text-[#050505] transition-colors hover:bg-[#3bc96a] disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap"
              >
                {isSendingMessage ? 'Sending...' : 'Send'}
              </button>
            </div>
            {newMessage.length > 450 && (
              <p className="mt-1 text-right text-xs text-gray-500">
                {newMessage.length} / 500 characters
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
